import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChessBoard, faEnvelope, faFont, faHome } from '@fortawesome/free-solid-svg-icons';

// Categories that have their own route pages
const ROUTED_CATEGORIES = ['people', 'play', 'learn', 'analysis', 'openings', 'books', 'events', 'new'];

const CHESS_SUB_FILTERS = ['new', 'social', 'play', 'learn', 'openings', 'analysis', 'mafia'];

const filters = [
  { id: 'home', label: '', icon: faChessBoard },
  {
    id: 'chess',
    label: 'apps',
    subFilters: [
      { id: 'new', label: 'New' },
      { id: 'social', label: 'Social' },
      { id: 'play', label: 'Play' },
      { id: 'learn', label: 'Learn' },
      { id: 'openings', label: 'Openings' },
      { id: 'analysis', label: 'Analysis' },
      { id: 'mafia', label: 'Mafia' }
    ]
  },
  { id: 'people', label: 'people' },
  { id: 'books', label: 'books' },
  { id: 'events', label: 'events' },
  { id: 'blog', label: 'blog' },
  { id: 'all', label: 'all' },
  { id: 'contact', label: '' }
];

const DirectoryHeader = ({
  activeCategory,       // Current active category slug (null or 'home' for homepage default)
  activeSubFilter,      // Current active sub-filter (for apps dropdown highlighting)
  onFilterChange,       // Optional callback for non-routed in-page filters (homepage uses this)
  onSearchChange,       // Optional callback for search query changes
  tabAlphabeticalState, // Optional alphabetical sort state object
  onToggleAlphabetical  // Optional callback to toggle alphabetical sort
}) => {
  const navigate = useNavigate();
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showMobileFilterModal, setShowMobileFilterModal] = useState(false);
  const [expandedApps, setExpandedApps] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  // Determine which top-level filter is "active" for highlighting
  const getActiveFilterId = () => {
    if (!activeCategory || activeCategory === 'home') return 'home';
    if (CHESS_SUB_FILTERS.includes(activeCategory)) return 'chess';
    return activeCategory;
  };

  const activeFilterId = getActiveFilterId();

  // Handle clicking a filter button or sub-filter
  const handleFilterClick = (filterId, subFilterId) => {
    setOpenDropdown(null);
    setShowMobileFilterModal(false);
    setExpandedApps(false);

    if (filterId === 'home') {
      if (onFilterChange) { onFilterChange('home'); }
      navigate('/');
    } else if (filterId === 'blog') {
      navigate('/blogs');
    } else if (filterId === 'contact') {
      navigate('/contact');
    } else if (subFilterId && ROUTED_CATEGORIES.includes(subFilterId)) {
      navigate('/' + subFilterId);
    } else if (!subFilterId && ROUTED_CATEGORIES.includes(filterId)) {
      navigate('/' + filterId);
    } else if (onFilterChange) {
      // Non-routed filter (all, social, professionals, mafia) — in-page filtering
      onFilterChange(filterId, subFilterId);
    } else {
      // On a category page with no onFilterChange, navigate home
      navigate('/');
    }
    scrollToTop();
  };

  const handleHomeClick = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setOpenDropdown(null);
    if (onSearchChange) onSearchChange('');
    handleFilterClick('home');
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (onSearchChange) onSearchChange(value);
  };

  const handleCloseSearch = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    if (onSearchChange) onSearchChange('');
  };

  const showAlphabetical = tabAlphabeticalState && onToggleAlphabetical && activeFilterId !== 'home';

  return (
    <>
      {/* Desktop Header */}
      <div
        className="w-full flex justify-between items-center px-16 mb-4 fixed top-0 left-0 right-0 z-40 bg-yellow-400 py-2"
      >
        {/* Left side - TrendingChess.com branding */}
        <div className="flex-column items-center cursor-pointer" onClick={handleHomeClick}>
          <h1 className="text-4xl md:text-5xl font-bold text-black">
            TrendingChess.com
          </h1>
        </div>

        {/* Right side - Navigation content */}
        <div
          className={`flex mt-4 mb-4 items-center ${!isSearchMode ? 'hidden md:inline-flex' : 'hidden md:flex'}`}
        >
          {isSearchMode ? (
            // Search Mode
            <div className="flex items-center gap-3 w-full max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search chess..."
                className="flex-1 bg-gray-800 border border-gray-600 rounded-full px-4 py-2 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all duration-200"
                autoFocus
              />
              <button
                onClick={handleCloseSearch}
                className="text-black hover:text-gray-700 transition-colors text-2xl font-bold"
                title="Close search"
              >
                ×
              </button>
            </div>
          ) : (
            // Desktop Filter Mode
            <div className="flex items-center justify-center gap-6">
              {filters.map((filter) => (
                <div key={filter.id} className="relative">
                  {filter.subFilters ? (
                    // Filter with dropdown
                    <div>
                      <button
                        onClick={() => {
                          setOpenDropdown(openDropdown === filter.id ? null : filter.id);
                        }}
                        className={`text-lg transition-colors cursor-pointer flex items-center gap-1 ${
                          activeFilterId === filter.id
                            ? 'text-black font-semibold'
                            : 'text-black hover:text-gray-700'
                        }`}
                      >
                        {filter.label}
                        <span className="text-xs">▼</span>
                      </button>
                      {/* Dropdown menu */}
                      {openDropdown === filter.id && (
                        <div className="absolute top-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50 min-w-[120px]">
                          {filter.subFilters.map((subFilter) => (
                            <button
                              key={subFilter.id}
                              onClick={() => handleFilterClick(filter.id, subFilter.id)}
                              className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                                activeSubFilter === subFilter.id || activeCategory === subFilter.id
                                  ? 'text-yellow-400 bg-gray-700'
                                  : 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                              }`}
                            >
                              {subFilter.label.toLowerCase()}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular filter button
                    <button
                      onClick={() => handleFilterClick(filter.id)}
                      className={`text-lg transition-colors cursor-pointer flex items-center gap-1 ${
                        activeFilterId === filter.id
                          ? 'text-black font-semibold'
                          : 'text-black hover:text-gray-700'
                      }`}
                    >
                      {filter.icon && <FontAwesomeIcon icon={filter.icon} className={filter.id === 'home' ? "text-2xl" : "text-xs"} />}
                      {filter.label && filter.label}
                    </button>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-3 ml-2">
                {showAlphabetical && (
                  <button
                    onClick={() => onToggleAlphabetical(activeFilterId)}
                    className={`transition-colors px-2 py-1 rounded ${
                      tabAlphabeticalState[activeFilterId]
                        ? 'text-white bg-black'
                        : 'text-black hover:text-gray-700'
                    }`}
                    title="Sort Alphabetically"
                  >
                    <FontAwesomeIcon icon={faFont} />
                  </button>
                )}
                <button
                  onClick={() => navigate('/contact')}
                  className="text-black hover:text-gray-700 transition-colors"
                  title="Contact"
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                </button>
                <button
                  onClick={() => setIsSearchMode(true)}
                  className="text-black hover:text-gray-700 transition-colors"
                  title="Search"
                >
                  <FontAwesomeIcon icon={faSearch} className="text-xl" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Simplified header layout */}
      <div
        className="flex md:hidden flex-col w-full mb-8 fixed top-0 left-0 right-0 z-40 bg-yellow-400 py-3 px-16 items-center text-center"
      >
        {isSearchMode ? (
          // Mobile Search Mode
          <div className="w-full py-4">
            <div className="flex items-center gap-3 max-w-sm mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search chess..."
                className="flex-1 bg-gray-800 border border-gray-600 rounded-full px-4 py-3 text-gray-100 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all duration-200"
                autoFocus
              />
              <button
                onClick={handleCloseSearch}
                className="text-black hover:text-gray-700 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center"
                title="Close search"
              >
                ×
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Title and Subtitle - Centered */}
            <div className="flex flex-col cursor-pointer mb-3" onClick={handleHomeClick}>
              <div className="flex items-center justify-center gap-2">
                {activeFilterId !== 'home' && (
                  <FontAwesomeIcon icon={faHome} className="text-lg text-black" />
                )}
                <h1 className="text-lg font-bold text-black">TrendingChess.com</h1>
              </div>
            </div>

            {/* Icons - Centered Below */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/contact')}
                className="text-black hover:text-gray-700 transition-colors"
                title="Contact"
              >
                <FontAwesomeIcon icon={faEnvelope} className="text-lg" />
              </button>
              <button
                onClick={() => setShowMobileFilterModal(true)}
                className="text-black hover:text-gray-700 transition-colors font-medium text-sm"
                title="Filter"
              >
                {activeFilterId === 'home' ? 'Filter' :
                 activeFilterId === 'chess' ? (activeSubFilter || activeCategory || 'Apps').charAt(0).toUpperCase() + (activeSubFilter || activeCategory || 'Apps').slice(1) :
                 activeFilterId.charAt(0).toUpperCase() + activeFilterId.slice(1)}
              </button>
              <button
                onClick={() => setIsSearchMode(true)}
                className="text-black hover:text-gray-700 transition-colors"
                title="Search"
              >
                <FontAwesomeIcon icon={faSearch} className="text-lg" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilterModal && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setShowMobileFilterModal(false);
              setExpandedApps(false);
            }}
          />

          {/* Modal Content */}
          <div className="absolute inset-x-8 top-24 bottom-8 bg-gray-900 rounded-lg shadow-xl overflow-hidden flex flex-col max-w-md mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white">Filter Menu</h2>
              <button
                onClick={() => {
                  setShowMobileFilterModal(false);
                  setExpandedApps(false);
                }}
                className="text-gray-400 hover:text-white transition-colors text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Filter Options */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {/* Home */}
                <button
                  onClick={() => handleFilterClick('home')}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    activeFilterId === 'home'
                      ? 'bg-yellow-400 text-black font-semibold'
                      : 'bg-gray-800 text-white hover:bg-gray-700'
                  }`}
                >
                  <FontAwesomeIcon icon={faChessBoard} className="text-lg" />
                  <span>Home</span>
                </button>

                {/* Apps with collapsible subcategories */}
                {filters.find(f => f.id === 'chess')?.subFilters && (
                  <div className="space-y-1">
                    <button
                      onClick={() => setExpandedApps(!expandedApps)}
                      className={`w-full p-3 rounded-lg transition-colors ${activeFilterId === 'chess' ? 'bg-yellow-400 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">📱</span>
                          <span className="font-semibold">Apps</span>
                        </div>
                        <span className={`transition-transform ${expandedApps ? 'rotate-180' : ''}`}>▼</span>
                      </div>
                    </button>

                    {expandedApps && (
                      <div className="ml-6 space-y-1">
                        {filters.find(f => f.id === 'chess').subFilters.map((subFilter) => (
                          <button
                            key={subFilter.id}
                            onClick={() => handleFilterClick('chess', subFilter.id)}
                            className={`w-full text-left p-2 rounded transition-colors ${
                              activeSubFilter === subFilter.id || activeCategory === subFilter.id
                                ? 'bg-yellow-400 text-black font-medium'
                                : 'text-gray-300 hover:text-white hover:bg-gray-700'
                            }`}
                          >
                            {subFilter.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Other main categories */}
                {filters.filter(f => !f.subFilters && f.id !== 'home' && f.label).map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => handleFilterClick(filter.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      activeFilterId === filter.id
                        ? 'bg-yellow-400 text-black font-semibold'
                        : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                  >
                    <span className="text-lg">
                      {filter.id === 'people' && '👥'}
                      {filter.id === 'books' && '📚'}
                      {filter.id === 'events' && '🏆'}
                      {filter.id === 'blog' && '📰'}
                      {filter.id === 'all' && '📋'}
                    </span>
                    <span className="capitalize">{filter.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DirectoryHeader;
